// 004 (FR-017, FR-024, FR-037, FR-048): a controlled confirm dialog on React Native Modal.
// Focus moves to Cancel on open; Escape (web) and the Android back button cancel. 44 px targets
// come from Button.
import { type Ref, useEffect, useRef } from "react";
import { Modal, Platform, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";

import { Button } from "./Button";
import { Text } from "./Text";

export type ConfirmDialogProps = {
  visible: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending?: boolean;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: t.space.lg,
      backgroundColor: t.color.scrim,
    },
    dialog: {
      width: 420,
      maxWidth: "100%",
      padding: t.space.x2l,
      gap: t.space.lg,
      backgroundColor: t.color.surface,
      borderRadius: t.radius.card,
    },
    actions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: t.space.sm,
    },
    action: { flexGrow: 0 },
  }),
);

type Focusable = { focus?: () => void };

export function ConfirmDialog({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  pending = false,
}: ConfirmDialogProps) {
  const styles = useStyles();
  const cancelRef = useRef<Focusable>(null);
  const cancelLatest = useRef(onCancel);
  useEffect(() => {
    cancelLatest.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    if (!visible) return;
    cancelRef.current?.focus?.();
    if (Platform.OS !== "web") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelLatest.current();
    };
    globalThis.document?.addEventListener("keydown", onKey);
    return () => globalThis.document?.removeEventListener("keydown", onKey);
  }, [visible]);

  if (!visible) return null;
  return (
    <Modal
      transparent
      visible
      animationType="fade"
      onRequestClose={onCancel}
      testID="confirm-dialog-modal"
    >
      <View style={styles.backdrop}>
        <View
          style={styles.dialog}
          role="alertdialog"
          aria-modal
          aria-label={title}
          testID="confirm-dialog"
        >
          <Text variant="heading" size="lg" accessibilityRole="header">
            {title}
          </Text>
          {message ? <Text tone="muted">{message}</Text> : null}
          <View style={styles.actions}>
            <Button
              variant="outline"
              ref={cancelRef as Ref<View>}
              onPress={onCancel}
              disabled={pending}
              testID="confirm-dialog-cancel"
            >
              {cancelLabel}
            </Button>
            <Button
              onPress={onConfirm}
              pending={pending}
              testID="confirm-dialog-confirm"
            >
              {confirmLabel}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}
